import { createContext, forwardRef, memo, useContext } from 'react';
import { SortAscendingIcon, SortDescendingIcon, SortIcon } from '@page/icons';

import { cn } from '../../lib/styles';
import { Slot } from '@radix-ui/react-slot';

type TableGridContextType = {
  columnsConfigClassName: string;
};

const TableGridContext = createContext<TableGridContextType>({ columnsConfigClassName: '' });

/**
 * Grid-based table container. Use instead of `<Table>` when rows need
 * non-semantic-table features (accordion expansion, arbitrary nesting, etc.)
 * while still sharing the design-system table visual treatment.
 *
 * @param columns - A Tailwind grid-cols class, e.g. `"grid-cols-[14%_22%_12%]"`
 */
const TableGridBase = (
  {
    columnsConfigClassName,
    className,
    children,
    ...rest
  }: React.ComponentProps<'div'> & { columnsConfigClassName: string },
  ref: React.Ref<HTMLDivElement>,
) => {
  return (
    <TableGridContext.Provider value={{ columnsConfigClassName }}>
      <div ref={ref} data-slot="table-grid" className={cn('relative w-full', className)} {...rest}>
        {children}
      </div>
    </TableGridContext.Provider>
  );
};

export const TableGrid = memo(forwardRef(TableGridBase));

function TableGridHeader({ className, ...rest }: React.ComponentProps<'div'>) {
  const { columnsConfigClassName } = useContext(TableGridContext);

  return (
    <div className={className}>
      <div
        data-slot="table-grid-header"
        className={cn(
          'grid items-center min-h-14 hover:bg-bg-sur-secondary transition-all px-2 rounded-sm',
          columnsConfigClassName,
        )}
        {...rest}
      />
      <div className="px-4">
        <div className="h-px w-full bg-border-default" />
      </div>
    </div>
  );
}

function TableGridBody({
  className,
  asChild,
  ...rest
}: React.ComponentProps<'div'> & {
  asChild?: boolean;
}) {
  const Component = asChild ? Slot : 'div';
  return <Component data-slot="table-grid-body" className={cn('flex flex-col gap-0', className)} {...rest} />;
}

const TableGridRowBase = ({
  className,
  rowClassName,
  selected,
  ...rest
}: React.ComponentProps<'div'> & {
  rowClassName?: string;
  selected?: boolean;
}) => {
  const { columnsConfigClassName } = useContext(TableGridContext);

  return (
    <div className={className}>
      <div
        data-slot="table-grid-row"
        className={cn(
          'grid items-center px-2',
          'hover:bg-bg-sur-secondary transition-all',
          'relative group min-h-14 rounded-sm',
          {
            'bg-bg-sur-secondary': selected,
          },
          columnsConfigClassName,
          rowClassName,
        )}
        {...rest}
      />
      <div className="px-4">
        <div className="h-px w-full bg-border-tertiary" />
      </div>
    </div>
  );
};

export const TableGridRow = memo(TableGridRowBase);

type SortDirection = 'asc' | 'desc' | null;

type TableGridHeadProps = React.ComponentProps<'div'> & {
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: () => void;
};

function TableGridHead({ className, children, sortable, sortDirection, onSort, ...rest }: TableGridHeadProps) {
  return (
    <div
      data-slot="table-grid-head"
      className={cn(
        'font-heading-l font-semibold text-text-primary min-h-14 px-2 text-left align-middle flex items-center truncate',
        sortable && 'cursor-pointer',
        className,
      )}
      onClick={sortable ? onSort : undefined}
      {...rest}
    >
      {children}
      {sortable && <SortIndicator direction={sortDirection} />}
    </div>
  );
}

function SortIndicator({ direction }: { direction?: SortDirection }) {
  if (direction === 'asc') return <SortAscendingIcon className="size-4 text-icon-secondary ml-1 shrink-0" />;

  if (direction === 'desc') return <SortDescendingIcon className="size-4 text-icon-secondary ml-1 shrink-0" />;

  return <SortIcon className="size-4 text-icon-secondary ml-1 shrink-0" />;
}

function TableGridCell({ className, ...rest }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="table-grid-cell"
      className={cn('font-body-md text-text-secondary px-2 align-middle truncate', className)}
      {...rest}
    />
  );
}

export { TableGridHeader, TableGridBody, TableGridHead, TableGridCell };
export type { SortDirection, TableGridHeadProps };
